import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BRAND } from "@/lib/avocado";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";

const PERKS = [
  "5% of Tom's commission on every completed sale",
  "Track every stage — valuation to completion",
  "No fees, no fine print, paid the moment the deal completes",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    const r = await register(name, email, password);
    setBusy(false);
    if (r.ok) {
      toast.success("You're in. Welcome to the avocado network 🥑");
      navigate("/dashboard");
    } else {
      toast.error(r.error || "Could not create account");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ background: "#FFFCF7" }}>
      <div className="relative hidden md:flex flex-col justify-between p-12" style={{ background: BRAND.pink }}>
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.14) 1.5px, transparent 1.5px)", backgroundSize: "22px 22px" }} />
        <div className="relative z-10">
          <BrandLogo tone="light" size={52} />
        </div>
        <div className="relative z-10 max-w-lg">
          <img
            src={BRAND.logoUrl}
            alt=""
            className="w-40 mb-8 rounded-2xl"
            style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.15)" }}
          />
          <p className="uppercase-label" style={{ color: "rgba(255,255,255,0.85)" }}>Join the network</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-3 leading-[0.98] text-white" style={{ fontWeight: 700 }}>
            One good <span className="italic" style={{ color: BRAND.green }}>referral.</span><br/>
            One proper thank-you.
          </h1>
          <ul className="mt-8 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-white/95">
                <span className="mt-0.5 rounded-full p-1" style={{ background: BRAND.green }}>
                  <Check size={13} strokeWidth={3} color="#2E3E0C" />
                </span>
                <span className="text-sm leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative z-10 text-xs" style={{ color: "rgba(255,255,255,0.75)" }}>Powered by avocado property · putting people before property since 2018</div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8"><BrandLogo /></div>
          <p className="uppercase-label" style={{ color: BRAND.pink }}>Create account</p>
          <h2 className="font-display text-4xl md:text-5xl mt-1 mb-2" style={{ fontWeight: 700 }}>
            Become a <span className="italic">referrer.</span>
          </h2>
          <p className="text-sm mb-8" style={{ color: "#7A6D5F" }}>Free to join. Submit your first lead in a minute.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div className="space-y-1.5">
              <Label className="uppercase-label">Full name</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith" className="rounded-xl h-12 bg-white border-2" data-testid="register-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="uppercase-label">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" className="rounded-xl h-12 bg-white border-2" data-testid="register-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="uppercase-label">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters" className="rounded-xl h-12 bg-white border-2" data-testid="register-password-input" />
            </div>
            <button type="submit" disabled={busy}
              className="btn-brand w-full h-12 rounded-full font-bold text-base flex items-center justify-center disabled:opacity-70"
              data-testid="register-submit-button">
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Create my account"}
            </button>
          </form>

          <p className="text-sm mt-6" style={{ color: "#7A6D5F" }}>
            Already have an account?{" "}
            <Link to="/login" className="font-bold underline underline-offset-4" style={{ color: BRAND.pink }} data-testid="register-to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
